import {
  Button,
  Tooltip,
  Grid,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Select,
  FormControl,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SearchIcon from '@mui/icons-material/Search';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import ExpenseFormDialog from 'components/popups/ExpenseFormDialog';
import ExpenseService from 'services/ExpenseService';
import ExpenseTypeService from 'services/ExpenseTypeService';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import setDelay from 'helpers/LoadingDelay';
import DataTable from 'components/utils/DataTable/';
import Info from 'components/utils/Alert/Info';
import styles from './styles.module.css';
import { SUBSCRIPTION_TYPE, USER_PERMISSIONS, SelectFilterStyle } from 'const';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function Expenses() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [expenses, setExpenses] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExpenseFormDialog, setShowExpenseFormDialog] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    startDate,
    endDate,
    selectedTypeIds: [],
  });
  const [filteredExpenses, setFilteredExpenses] = useState(null);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);

  const subscriptionType = shop?.subscription?.type;
  const userHasWritePermission = user?.shop_permissions?.expense_permission === USER_PERMISSIONS.WRITE;

  const _getLoadExpenseRequestParams = useCallback(() => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('EXPENSE');
    return {
      start_date: DateTimeHelpers.getTimezoneOffsetDate(dynamicStartDate),
      end_date: DateTimeHelpers.getTimezoneOffsetDate(dynamicEndDate),
      type_ids: selectedTypeIds.length > 0 ? selectedTypeIds.join(',') : undefined,
    };
  }, [selectedTypeIds]);

  const fetchExpenseTypes = useCallback(async () => {
    try {
      const res = await ExpenseTypeService.getExpenseTypes();
      setExpenseTypes(res);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  }, [postAlertMessage]);

  useEffect(() => {
    fetchExpenseTypes();
  }, [fetchExpenseTypes]);

  const getExpenses = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const params = _getLoadExpenseRequestParams();
      const res = await ExpenseService.getExpenses(params);
      setExpenses(res);
      setFilteredExpenses(res);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setDelay(setLoadingIndicator);
  }, [_getLoadExpenseRequestParams, postAlertMessage]);

  const generateExpenses = useCallback(() => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('EXPENSE');

    setStartDate(dynamicStartDate);
    setEndDate(dynamicEndDate);

    getExpenses();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      startDate: dynamicStartDate,
      endDate: dynamicEndDate,
      selectedTypeIds: selectedTypeIds,
    });
  }, [getExpenses, selectedTypeIds]);

  const handleExportToExcel = async () => {
    const startDateStr = DateTimeHelpers.getDateYMD(startDate);
    const endDateStr = DateTimeHelpers.getDateYMD(endDate);

    const csv = CsvHelper.getString(filteredExpenses, {
      id: 'ID',
      date: {
        title: 'Date',
        getValue: (expense) => DateTimeHelpers.convertDateToIsoDMY(expense.date),
      },
      description: 'Description',
      amount: 'Amount',
    });
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    saveAs(blob, `EXPENSES-${startDateStr}-${endDateStr}.csv`);

    postAlertMessage({ text: 'Exported to CSV successfully', type: 'success' });
  };

  const _getExpenseAmountSum = (expenses) => {
    if (!expenses || !expenses.length) {
      return 0;
    }

    let sum = 0;
    expenses.forEach((expenses) => (sum += expenses.amount));
    return ShopsHelper.getAmountFormatted(shop, sum);
  };

  const handleDeleteClick = (data) => {
    setSelectedExpense(data);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = (data) => {
    setSelectedExpense(null);
    setShowDeleteConfirm(false);
  };

  const handleDeleteExpense = async () => {
    setLoadingIndicator(true);
    try {
      await ExpenseService.deleteExpense(selectedExpense.id);
      setExpenses(expenses.filter((expense) => expense.id !== selectedExpense.id));
      postAlertMessage({
        text: 'Expense deleted successfully',
        type: 'success',
      });
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setDelay(setLoadingIndicator);
  };

  const handleNewExpenseClick = () => {
    setShowExpenseFormDialog(true);
  };

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setShowExpenseFormDialog(true);
  };

  const handleFormCancel = () => {
    setShowExpenseFormDialog(false);
    setSelectedExpense(null);
  };

  const handleFormSuccess = () => {
    setShowExpenseFormDialog(false);
    generateExpenses();
  };

  /// Obsolete methods
  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredExpenses(
      expenses.filter(
        (item) =>
          item.id.toString().includes(value.toLowerCase()) ||
          (item.date && DateTimeHelpers.convertDateToIsoDMY(item.date).includes(value.toLowerCase())) ||
          (item.description && item.description.toLowerCase().includes(value.toLowerCase()))
      )
    );
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'callback',
      viewRender: (obj) => {
        return DateTimeHelpers.convertDateToIsoDMY(obj.date);
      },
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'text',
    },
    {
      label: tCommon('Nature'),
      id: 'type',
      type: 'callback',
      viewRender: (obj) => {
        return obj.amount >= 0 ? 'Expense' : 'Income';
      },
    },
    {
      label: tCommon('Expense Type'),
      id: 'type_id',
      type: 'callback',
      viewRender: (obj) => {
        return obj.type ? obj.type.name : '-';
      },
    },
    {
      label: tCommon('Amount'),
      id: 'amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            {userHasWritePermission && (
              <>
                <Tooltip title={tOperations('Edit category')} onClick={() => handleEditClick(item)}>
                  <EditIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tOperations('Delete category')} onClick={() => handleDeleteClick(item)}>
                  <DeleteIcon className={styles.iconButton} />
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    },
  ];

  const isAnyChangeOnReportsFilters = useCallback(() => {
    const typeIdsChanged = JSON.stringify(selectedTypeIds) !== JSON.stringify(prevFilterData.selectedTypeIds);
    return startDate !== prevFilterData.startDate || endDate !== prevFilterData.endDate || typeIdsChanged;
  }, [
    startDate,
    endDate,
    selectedTypeIds,
    prevFilterData.startDate,
    prevFilterData.endDate,
    prevFilterData.selectedTypeIds,
  ]);

  useEffect(() => {
    if (shop?.expense_validity && !featureIsExpired(shop.expense_validity)) {
      generateExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const expenseAmountSum = _getExpenseAmountSum(filteredExpenses);
  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Expense')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
        {subscriptionType &&
          subscriptionType === SUBSCRIPTION_TYPE.PREMIUM &&
          !featureIsExpired(shop.expense_validity) && (
            <div style={{ justifyContent: 'flex-end' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Export As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a', marginRight: 5 }}
                onClick={handleExportToExcel}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('CSV')}
              </Button>
            </div>
          )}
      </div>
      {featureIsExpired(shop?.expense_validity) && (
        <RestrictionInfo title={tCommon('Feature not available')} content={tOperations('No expense feature')} />
      )}
      {subscriptionType &&
        subscriptionType === SUBSCRIPTION_TYPE.PREMIUM &&
        !featureIsExpired(shop.expense_validity) && (
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tOperations('Expense Report for {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(startDate),
                    to: DateTimeHelpers.convertDateToDMY(endDate),
                  })}
                </h2>
              </div>

              <div className={styles.filerInputSec}>
                {userHasWritePermission && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ backgroundColor: '#ff851b' }}
                    className={styles.actionBtn}
                    onClick={handleNewExpenseClick}
                  >
                    <NoteAddIcon className={styles.actionBtnIcon} />
                    {tOperations('New Expense')}
                  </Button>
                )}
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search items')}
                  />
                  <Tooltip title="Search" placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange setFilterFromDate={setStartDate} setFilterToDate={setEndDate} storageKey="EXPENSE" />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Expense Type')}</label>
                </div>
                <div>
                  <FormControl >
                    <Select
                      multiple
                      displayEmpty
                      value={selectedTypeIds}
                      onChange={(e) => {
                        setSelectedTypeIds(e.target.value);
                      }}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(selected) => {
                        if (selected.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = selected
                          .map((id) => expenseTypes.find((t) => t.id === id)?.name)
                          .filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {expenseTypes?.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          <Checkbox checked={selectedTypeIds.includes(type.id)} color="primary" />
                          <ListItemText primary={type.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Generate Report')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateExpenses}
                    disabled={isShowGenerateButton === false}
                  >
                    <SyncIcon className={styles.actionBtnIcon} />
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

      {subscriptionType &&
        subscriptionType === SUBSCRIPTION_TYPE.PREMIUM &&
        shop.expense_validity !== null &&
        (isShowGenerateButton === null ? (
          <Info title={tOperations('Set filters info title')} content={tOperations('Set filters expense info content')} />
        ) : (expenses || loadingIndicator) &&
        ((filteredExpenses && filteredExpenses.length) || loadingIndicator ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredExpenses || expenses || []}
              isLoading={loadingIndicator}
              summary={{ type: tCommon('Total'), amount: expenseAmountSum }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              title={tOperations('No expenses with the current filter')}
              content={tOperations(
                'You have no expenses to list with current filter configuration. Please clear the filters or create a expenses'
              )}
            />
          )
        )))}
      {showExpenseFormDialog && (
        <ExpenseFormDialog
          expense={selectedExpense}
          expenseTypes={expenseTypes}
          handleClose={handleFormCancel}
          handleSuccess={handleFormSuccess}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmPopup
          data={selectedExpense}
          handleClose={handleDeleteCancel}
          handleOkay={handleDeleteExpense}
          alertTitle={tCommon('Confirm Delete')}
          successBtnName={tCommon('Delete')}
          alertContent={tCommon('Are you sure you want to delete this expense?')}
        />
      )}
    </div>
  );
}

export default withConsoleBase(Expenses);
